import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { Save, TrendingUp, Package, ShoppingBag } from "lucide-react";

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function pence(p: number) {
  return `£${(p / 100).toFixed(2)}`;
}

function ago(d: string | null | undefined) {
  if (!d) return "—";
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface TipRow {
  type: "food" | "hub_dash";
  orderId: number | null;
  requestId: number | null;
  driverId: number | null;
  driverName: string | null;
  storeName: string | null;
  grossPence: number;
  driverNetPence: number;
  platformCutPence: number;
  feePaidBy: "platform" | "connected";
  date: string | null;
}

interface TipSettings {
  tipFlatFeePence: number;
  tipPercent: number;
}

export default function AdminTips() {
  const { toast } = useToast();
  const [tips, setTips] = useState<TipRow[]>([]);
  const [totals, setTotals] = useState({ totalGrossPence: 0, totalDriverNetPence: 0, totalPlatformCutPence: 0 });
  const [settings, setSettings] = useState<TipSettings>({ tipFlatFeePence: 20, tipPercent: 5 });
  const [settingsForm, setSettingsForm] = useState<TipSettings>({ tipFlatFeePence: 20, tipPercent: 5 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/tips", { headers: authHeaders() });
      const data = await res.json() as { tips: TipRow[]; totals: typeof totals; settings: TipSettings };
      setTips(data.tips ?? []);
      setTotals(data.totals ?? { totalGrossPence: 0, totalDriverNetPence: 0, totalPlatformCutPence: 0 });
      setSettings(data.settings ?? { tipFlatFeePence: 20, tipPercent: 5 });
      setSettingsForm(data.settings ?? { tipFlatFeePence: 20, tipPercent: 5 });
    } catch {
      toast({ title: "Failed to load tips", variant: "destructive", action: <ToastAction altText="Reload page" onClick={() => window.location.reload()}>Reload</ToastAction> });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  async function saveSettings() {
    setSaving(true);
    try {
      const res = await fetch("/api/admin/item-delivery-settings", {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ tipFlatFeePence: settingsForm.tipFlatFeePence, tipPercent: settingsForm.tipPercent }),
      });
      if (!res.ok) throw new Error();
      setSettings(settingsForm);
      toast({ title: "Tip split saved" });
      void load();
    } catch {
      toast({ title: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  const preview = (gross: number) => {
    const cut = settingsForm.tipFlatFeePence + Math.floor((gross * settingsForm.tipPercent + 50) / 100);
    const net = Math.max(0, gross - cut);
    return { cut, net };
  };

  const exampleGross = 200;
  const { cut: exCut, net: exNet } = preview(exampleGross);

  return (
    <AdminLayout current="/admin/tips">
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-primary mb-1">Tips</h1>
          <p className="text-sm text-slate-500">Configure the platform tip split and view all tips received.</p>
        </div>

        {/* Settings card */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 max-w-lg space-y-5">
          <h2 className="text-base font-semibold text-slate-800">Platform tip split</h2>
          <p className="text-sm text-slate-500">
            Applies to all tips — food orders and Hub Dash. The platform keeps a flat fee plus a percentage.
            Drivers receive the remainder.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label htmlFor="tip-flat-fee" className="text-sm font-medium text-slate-700">Flat fee (pence)</label>
              <p className="text-xs text-slate-400">e.g. 20 = 20p per tip</p>
              <input
                id="tip-flat-fee"
                type="number" min="0" step="1"
                value={settingsForm.tipFlatFeePence}
                onChange={e => setSettingsForm(s => ({ ...s, tipFlatFeePence: parseInt(e.target.value) || 0 }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="tip-percent" className="text-sm font-medium text-slate-700">Percentage (%)</label>
              <p className="text-xs text-slate-400">e.g. 5 = 5% of tip gross</p>
              <input
                id="tip-percent"
                type="number" min="0" max="100" step="1"
                value={settingsForm.tipPercent}
                onChange={e => setSettingsForm(s => ({ ...s, tipPercent: parseInt(e.target.value) || 0 }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>
          <div className="bg-slate-50 rounded-lg px-4 py-3 text-sm text-slate-600 space-y-1">
            <p className="font-medium text-slate-700">Example — £2.00 tip</p>
            <p>Platform keeps: <span className="font-semibold text-indigo-700">{pence(exCut)}</span> ({settingsForm.tipFlatFeePence}p + {settingsForm.tipPercent}%)</p>
            <p>Driver receives: <span className="font-semibold text-green-700">{pence(exNet)}</span></p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => void saveSettings()}
              disabled={saving || (settingsForm.tipFlatFeePence === settings.tipFlatFeePence && settingsForm.tipPercent === settings.tipPercent)}
              className="flex items-center gap-2 bg-primary text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              <Save size={14} />
              {saving ? "Saving…" : "Save split"}
            </button>
            <span className="text-xs text-slate-400">Current: {settings.tipFlatFeePence}p + {settings.tipPercent}%</span>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs text-slate-500 mb-1">Total tips received (gross)</p>
            <p className="text-2xl font-bold text-slate-800">{pence(totals.totalGrossPence)}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs text-slate-500 mb-1">Platform kept</p>
            <p className="text-2xl font-bold text-indigo-700">{pence(totals.totalPlatformCutPence)}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs text-slate-500 mb-1">Paid to drivers (net)</p>
            <p className="text-2xl font-bold text-green-700">{pence(totals.totalDriverNetPence)}</p>
          </div>
        </div>

        {/* Tips table */}
        {loading ? (
          <div className="h-48 bg-slate-100 rounded-xl animate-pulse" />
        ) : tips.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <TrendingUp className="mx-auto mb-3 text-slate-300" size={36} />
            <p className="text-slate-500 font-medium">No tips recorded yet</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Type</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Job</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Driver</th>
                  <th className="px-4 py-2.5 text-right font-semibold text-slate-600">Gross tip</th>
                  <th className="px-4 py-2.5 text-right font-semibold text-slate-600">Platform kept</th>
                  <th className="px-4 py-2.5 text-right font-semibold text-slate-600">Driver net</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Stripe fees paid by</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tips.map((t, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <span className={`flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full w-fit ${t.type === "food" ? "bg-orange-50 text-orange-600" : "bg-blue-50 text-blue-600"}`}>
                        {t.type === "food" ? <ShoppingBag size={11} /> : <Package size={11} />}
                        {t.type === "food" ? "Food" : "Hub Dash"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs max-w-[200px] truncate">
                      {t.type === "food" ? (
                        <span>Order #{t.orderId} · {t.storeName ?? "—"}</span>
                      ) : (
                        <span>Hub Dash #{t.requestId}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-slate-700">{t.driverName ?? <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-slate-700">{pence(t.grossPence)}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-indigo-700">{pence(t.platformCutPence)}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-green-700">{pence(t.driverNetPence)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${t.feePaidBy === "connected" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                        {t.feePaidBy === "connected" ? "Connected account" : "Platform (us)"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-400 whitespace-nowrap">{ago(t.date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
